/**
 * Per-seat views of the game. This is the ONLY game data that leaves the server.
 *
 * A view never contains another player's cards, the face-down kitty, the
 * dealer's discard (except for the dealer), the RNG state, or the seed.
 * Bots are driven from these same views, so they cannot cheat either.
 */

import { type Card, type PlayedCard, type Seat, type Suit, type Team, SEATS } from './cards.js';
import {
  type CompletedTrick,
  type GameAction,
  type GameState,
  type HandResult,
  type HandSummary,
  type LogEntry,
  type Phase,
  type UpCardStatus,
  legalActions,
} from './game.js';
import type { RulesConfig } from './rules.js';

export interface PlayerView {
  /** Seat this view is for; null for spectators. */
  seat: Seat | null;
  rules: RulesConfig;
  phase: Phase;
  version: number;
  scores: [number, number];
  handNumber: number;
  dealNumber: number;
  dealer: Seat;
  turn: Seat | null;
  myHand: Card[];
  handCounts: [number, number, number, number];
  upCard: Card;
  upCardStatus: UpCardStatus;
  trump: Suit | null;
  maker: Seat | null;
  callRound: 1 | 2 | null;
  makerAlone: boolean;
  defenderAlone: Seat | null;
  sittingOut: Seat[];
  farmerSwapSeat: Seat | null;
  leader: Seat | null;
  currentTrick: PlayedCard[];
  tricks: CompletedTrick[];
  tricksWon: [number, number];
  /** Cards this seat knows are buried in the kitty / discarded (own farmer swap, own discard). */
  myBuriedCards: Card[];
  /** Legal actions for this seat (empty unless it is this seat's turn). */
  legalActions: GameAction[];
  log: LogEntry[];
  previousHandLog: LogEntry[];
  history: HandSummary[];
  result: HandResult | null;
  winner: Team | null;
}

export function viewFor(state: GameState, seat: Seat | null): PlayerView {
  const h = state.hand;
  const buried: Card[] = [];
  if (seat !== null) {
    if (h.farmerSwapSeat === seat) buried.push(...h.farmerSwappedOut);
    if (seat === h.dealer && h.discard) buried.push(h.discard);
  }
  const copy = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
  return {
    seat,
    rules: { ...state.rules },
    phase: state.phase,
    version: state.version,
    scores: [...state.scores] as [number, number],
    handNumber: state.handNumber,
    dealNumber: h.dealNumber,
    dealer: h.dealer,
    // During deal options only the deciding player learns it's their decision, so a
    // kept farmer's hand doesn't reveal who holds it.
    turn:
      state.phase === 'hand_complete' || state.phase === 'game_over' || (state.phase === 'deal_options' && h.turn !== seat)
        ? null
        : h.turn,
    myHand: seat === null ? [] : [...h.hands[seat]],
    handCounts: SEATS.map((s) => h.hands[s].length) as [number, number, number, number],
    upCard: h.upCard,
    upCardStatus: h.upCardStatus,
    trump: h.trump,
    maker: h.maker,
    callRound: h.callRound,
    makerAlone: h.makerAlone,
    defenderAlone: h.defenderAlone,
    sittingOut: [...h.sittingOut],
    farmerSwapSeat: h.farmerSwapSeat,
    leader: h.leader,
    currentTrick: copy(h.currentTrick),
    tricks: copy(h.tricks),
    tricksWon: [...h.tricksWon] as [number, number],
    myBuriedCards: buried,
    legalActions: seat === null ? [] : legalActions(state, seat),
    log: copy(h.log),
    previousHandLog: copy(state.previousHandLog),
    history: copy(state.history),
    result: h.result ? { ...h.result } : null,
    winner: state.winner,
  };
}
